import React from 'react';
import { X } from 'lucide-react';
import { formatRelativeTime } from '../utils/utils';

export interface AppNotification {
  id: number;
  kind: string;
  title: string;
  body: string;
  project: number | null;
  blueprint: number | null;
  combination?: number | null;
  artifact?: number | null;
  project_name?: string;
  blueprint_name?: string;
  job_to_be_done?: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationsPanelProps {
  notifications: AppNotification[];
  onClose: () => void;
  onOpenNotification: (notification: AppNotification) => void;
  onMarkAllRead: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  notifications,
  onClose,
  onOpenNotification,
  onMarkAllRead,
}) => {
  return (
    <div
      className="fixed right-6 top-16 z-[12000] flex flex-col bg-white rounded-lg shadow-xl border border-[#E0DEE8] overflow-hidden"
      style={{ width: 333, height: 323 }}
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E0DEE8] shrink-0">
        <h2 className="text-base font-bold text-gray-900">Notifications</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-label="Close notifications"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {notifications.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">No notifications yet.</p>
        ) : (
          notifications.map((notification, index) => (
            <div
              key={notification.id}
              className={`px-4 py-3 ${index > 0 ? 'border-t border-[#EEEEEE]' : ''}`}
            >
              <div className="flex items-start gap-2.5">
                <span
                  className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${
                    notification.is_read ? 'bg-gray-300' : 'bg-red-500'
                  }`}
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-bold text-gray-900 leading-snug">
                      {notification.title}
                    </p>
                    <button
                      type="button"
                      onClick={() => onOpenNotification(notification)}
                      className="text-xs text-[#4f9dc3] underline shrink-0 hover:text-[#3d85a8]"
                    >
                      Open
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">{notification.body}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatRelativeTime(notification.created_at)}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-[#E0DEE8] shrink-0 flex justify-end">
        <button
          type="button"
          onClick={onMarkAllRead}
          className="text-xs text-[#4f9dc3] underline hover:text-[#3d85a8] disabled:opacity-50 disabled:no-underline"
          disabled={notifications.every((n) => n.is_read)}
        >
          Mark all as read
        </button>
      </div>
    </div>
  );
};

export default NotificationsPanel;
